import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Loader2, Image, X } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const CATEGORY_OPTIONS = ['Gottesdienst', 'Lobpreis', 'Jugend', 'Gemeinschaft', 'Taufe', 'Sonstiges'];

const AdminGalleryTab = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [category, setCategory] = useState('Gottesdienst');
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => { fetchPhotos(); }, []);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/gallery`);
      const data = await res.json();
      setPhotos(data || []);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('caption', caption);
      formData.append('category', category);
      const res = await fetch(`${API_BASE}/gallery/upload`, { method: 'POST', headers: getAuthHeaders(), body: formData });
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || 'Upload fehlgeschlagen');
      } else {
        setCaption('');
        await fetchPhotos();
      }
    } catch {
      alert('Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Dieses Foto wirklich loeschen?')) return;
    try {
      await fetch(`${API_BASE}/gallery/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      setPhotos(prev => prev.filter(p => p.id !== id));
    } catch {
      alert('Fehler beim Loeschen');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Foto-Galerie ({photos.length} Fotos)</h2>
      </div>

      {/* Upload Form */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6" data-testid="gallery-upload-form">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Neues Foto hochladen</h3>
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bildunterschrift</label>
            <input type="text" value={caption} onChange={e => setCaption(e.target.value)}
              data-testid="gallery-caption"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="z.B. Gottesdienst Oktober 2025" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              data-testid="gallery-category"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload}
              className="hidden" id="photo-upload" data-testid="gallery-file-input" />
            <label htmlFor="photo-upload"
              className={`w-full px-5 py-2.5 rounded-lg font-medium text-center cursor-pointer transition-all flex items-center justify-center gap-2 ${
                uploading ? 'bg-gray-100 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" />Wird hochgeladen...</> : <><Upload className="w-4 h-4" />Foto hochladen</>}
            </label>
          </div>
        </div>
        <p className="text-xs text-gray-500">Erlaubte Formate: JPEG, PNG, WebP, GIF. Max. 10 MB.</p>
      </div>

      {/* Photo Grid */}
      {photos.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center text-gray-500">
          <Image className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">Noch keine Fotos vorhanden</p>
          <p className="text-sm mt-1">Lade das erste Foto eurer Gemeinde hoch.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="gallery-grid">
          {photos.map(photo => (
            <div key={photo.id} className="relative group rounded-xl overflow-hidden shadow-md bg-gray-100 aspect-square"
              data-testid={`gallery-photo-${photo.id}`}>
              <img src={`${API_BASE}/gallery/image/${photo.id}`} alt={photo.caption || 'Foto'}
                className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors">
                <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform">
                  <p className="text-white text-sm font-medium truncate">{photo.caption || photo.original_filename}</p>
                  <p className="text-white/70 text-xs">{photo.category}</p>
                </div>
                <button onClick={() => handleDelete(photo.id)}
                  data-testid={`delete-photo-${photo.id}`}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGalleryTab;
